import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { C, S } from '../../../../utils/styles';
import Icon from '../../../Icon';
import { useToast } from '../../../Toast';
import {
  getNewsletter,
  createNewsletter,
  updateNewsletter,
  previewNewsletter,
  testSendNewsletter,
  sendNewsletter,
  getAudienceCount,
} from './newsletterApi';

// ============================================================================
// NewsletterEditor — structured monthly newsletter builder (Phase 19.1)
// ============================================================================
// Renders a 2-column layout: editor (left) + live HC-branded preview (right).
// Lives inside NewsletterPanel; receives optional `newsletterId` (existing draft)
// or null (start fresh).
// ============================================================================

function tempStoryId() {
  return `story-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

const BLANK_NL = {
  status: 'draft',
  issueTitle: 'HealthCues Monthly',
  issueMonth: new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }),
  tagline: '',
  heroImageUrl: '',
  intro: { heading: 'From the team', body: '' },
  stories: [],
  whatsComing: { heading: 'Coming up next month', items: [''] },
  footer: { tagline: 'Smarter benefits. Bigger savings. Real outcomes.', contactLink: 'mailto:hello@healthcues.com' },
};

export default function NewsletterEditor({ newsletterId, onBack, onChanged }) {
  const showToast = useToast();
  const [nl, setNl] = useState(null);                // working draft (in-memory)
  const [serverId, setServerId] = useState(newsletterId || null);
  const [loading, setLoading] = useState(!!newsletterId);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [audience, setAudience] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const previewTimer = useRef(null);

  const isSent = nl?.status === 'sent';

  // Initial load
  useEffect(() => {
    let mounted = true;
    if (newsletterId) {
      setLoading(true);
      getNewsletter(newsletterId)
        .then((d) => {
          if (!mounted) return;
          setNl({ ...BLANK_NL, ...d, intro: { ...BLANK_NL.intro, ...(d.intro || {}) }, whatsComing: { ...BLANK_NL.whatsComing, ...(d.whatsComing || {}), items: (d.whatsComing?.items || ['']) }, footer: { ...BLANK_NL.footer, ...(d.footer || {}) } });
          setPreviewHtml(d.renderedHtml || '');
          setDirty(false);
        })
        .catch((err) => showToast(err.message || 'Failed to load newsletter', 'error'))
        .finally(() => { if (mounted) setLoading(false); });
    } else {
      setNl({ ...BLANK_NL, stories: [emptyStory()] });
    }
    return () => { mounted = false; };
  }, [newsletterId, showToast]);

  // Load audience count once
  useEffect(() => {
    getAudienceCount().then(setAudience).catch(() => {});
  }, []);

  // Debounced server-side preview (Mailgun-identical HTML)
  useEffect(() => {
    if (!serverId) {
      // For brand-new newsletters we render a local lightweight preview only.
      return;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const data = await previewNewsletter(serverId);
        setPreviewHtml(data.html || '');
      } catch (_) {
        // silent — preview is best-effort
      } finally {
        setPreviewLoading(false);
      }
    }, 600);
    return () => clearTimeout(previewTimer.current);
  }, [serverId, nl]);

  const patch = (updater) => {
    setNl((prev) => (prev ? (typeof updater === 'function' ? updater(prev) : { ...prev, ...updater }) : prev));
    setDirty(true);
  };

  // ---- Build payload for server ----
  const toPayload = useCallback(() => {
    if (!nl) return null;
    return {
      issueTitle: nl.issueTitle || '',
      issueMonth: nl.issueMonth || '',
      tagline: nl.tagline || '',
      heroImageUrl: nl.heroImageUrl || '',
      intro: { heading: nl.intro?.heading || '', body: nl.intro?.body || '' },
      stories: (nl.stories || []).map((s) => ({
        id: s.id,
        heading: s.heading || '',
        body: s.body || '',
        imageUrl: s.imageUrl || '',
        ctaLabel: s.ctaLabel || '',
        ctaUrl: s.ctaUrl || '',
      })),
      whatsComing: {
        heading: nl.whatsComing?.heading || '',
        items: (nl.whatsComing?.items || []).map((s) => (s || '').trim()).filter(Boolean),
      },
      footer: {
        tagline: nl.footer?.tagline || '',
        contactLink: nl.footer?.contactLink || '',
      },
    };
  }, [nl]);

  const saveDraft = async () => {
    if (!nl) return;
    if (!(nl.issueTitle || '').trim()) {
      showToast('Issue title is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload();
      let fresh;
      if (!serverId) {
        fresh = await createNewsletter(payload);
        setServerId(fresh.id);
      } else {
        fresh = await updateNewsletter(serverId, payload);
      }
      setNl((prev) => ({ ...prev, ...fresh, intro: { ...prev.intro, ...(fresh.intro || {}) }, whatsComing: { ...prev.whatsComing, ...(fresh.whatsComing || {}) }, footer: { ...prev.footer, ...(fresh.footer || {}) } }));
      setPreviewHtml(fresh.renderedHtml || previewHtml);
      setDirty(false);
      showToast('Draft saved', 'success');
      onChanged && onChanged();
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    if (!serverId) {
      showToast('Save the draft first, then send a test', 'info');
      return;
    }
    if (dirty) {
      await saveDraft();
    }
    setTesting(true);
    try {
      const r = await testSendNewsletter(serverId);
      showToast(`Test newsletter sent to ${r.sentTo}`, 'success');
    } catch (err) {
      showToast(err.message || 'Test send failed', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleSend = async () => {
    if (!serverId || dirty) {
      await saveDraft();
    }
    setSending(true);
    try {
      const r = await sendNewsletter(serverId);
      showToast(`Newsletter sent to ${r.recipientCount} recipients`, 'success');
      setConfirmOpen(false);
      // Reload from server (now locked)
      const fresh = await getNewsletter(serverId);
      setNl({ ...BLANK_NL, ...fresh, intro: { ...BLANK_NL.intro, ...(fresh.intro || {}) }, whatsComing: { ...BLANK_NL.whatsComing, ...(fresh.whatsComing || {}), items: (fresh.whatsComing?.items || ['']) }, footer: { ...BLANK_NL.footer, ...(fresh.footer || {}) } });
      setPreviewHtml(fresh.renderedHtml || '');
      setDirty(false);
      onChanged && onChanged();
    } catch (err) {
      const more = err.providerError ? ` (${err.providerError})` : '';
      showToast(`Send failed: ${err.message}${more}`, 'error');
    } finally {
      setSending(false);
    }
  };

  // ---- Story operations ----
  const addStory = () => patch((p) => ({ ...p, stories: [...(p.stories || []), emptyStory()] }));
  const removeStory = (id) => patch((p) => ({ ...p, stories: (p.stories || []).filter((s) => s.id !== id) }));
  const patchStory = (id, fields) => patch((p) => ({
    ...p,
    stories: (p.stories || []).map((s) => (s.id === id ? { ...s, ...fields } : s)),
  }));
  const moveStory = (id, dir) => patch((p) => {
    const list = [...(p.stories || [])];
    const idx = list.findIndex((s) => s.id === id);
    if (idx < 0) return p;
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= list.length) return p;
    [list[idx], list[swap]] = [list[swap], list[idx]];
    return { ...p, stories: list };
  });

  // ---- What's Coming items ----
  const patchWcItem = (i, value) => patch((p) => ({
    ...p,
    whatsComing: { ...p.whatsComing, items: (p.whatsComing.items || []).map((v, idx) => (idx === i ? value : v)) },
  }));
  const addWcItem = () => patch((p) => ({
    ...p,
    whatsComing: { ...p.whatsComing, items: [...(p.whatsComing.items || []), ''] },
  }));
  const removeWcItem = (i) => patch((p) => ({
    ...p,
    whatsComing: { ...p.whatsComing, items: (p.whatsComing.items || []).filter((_, idx) => idx !== i) },
  }));

  if (loading || !nl) {
    return (
      <div style={S.card} data-testid="newsletter-editor-loading">
        <div style={S.heading}>Newsletter</div>
        <div style={{ color: C.textMid }}>Loading…</div>
      </div>
    );
  }

  return (
    <div data-testid="newsletter-editor" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(380px, 520px)', gap: 16, alignItems: 'start' }}>
      {/* LEFT: Editor */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Status / action bar */}
        <div style={{ ...S.card, position: 'sticky', top: 0, zIndex: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="newsletter-back"
              onClick={() => {
                if (dirty && !window.confirm('Discard unsaved changes?')) return;
                onBack && onBack();
              }}
              style={{ ...S.buttonSecondary, padding: '8px 14px' }}
            >
              <Icon name="arrow-back-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
              All newsletters
            </button>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {isSent ? `Sent ${nl.sentAt ? new Date(nl.sentAt).toLocaleString() : ''}` : 'Draft'}
                {' '}· {audience ? `${audience.total} recipients` : 'loading audience…'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
                {nl.issueTitle || '(untitled)'}{nl.issueMonth ? ` · ${nl.issueMonth}` : ''}
              </div>
            </div>
            <button
              type="button"
              data-testid="newsletter-save-btn"
              disabled={!dirty || saving || isSent}
              onClick={saveDraft}
              style={{ ...S.buttonSecondary, opacity: (!dirty || saving || isSent) ? 0.5 : 1 }}
            >
              {saving ? 'Saving…' : (dirty ? 'Save draft' : (isSent ? 'Locked' : 'Saved'))}
            </button>
            <button
              type="button"
              data-testid="newsletter-test-btn"
              disabled={!serverId || testing || isSent}
              onClick={handleTestSend}
              style={{ ...S.buttonSecondary, opacity: (!serverId || testing || isSent) ? 0.5 : 1 }}
              title={!serverId ? 'Save the draft first' : 'Send a test to your inbox'}
            >
              <Icon name="paper-plane-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
              {testing ? 'Sending…' : 'Send test to me'}
            </button>
            <button
              type="button"
              data-testid="newsletter-send-btn"
              disabled={sending || isSent}
              onClick={() => { if (!(nl.issueTitle || '').trim()) { showToast('Issue title is required', 'error'); return; } setConfirmOpen(true); }}
              style={{ ...S.buttonPrimary, opacity: (sending || isSent) ? 0.5 : 1 }}
            >
              <Icon name="send-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
              {isSent ? 'Already sent' : `Send to ${audience ? audience.total : 'everyone'}`}
            </button>
          </div>
        </div>

        {/* Issue meta */}
        <SectionCard step="1" title="Issue details" disabled={isSent}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <Field label="Issue title *">
              <input data-testid="nl-issue-title" type="text" value={nl.issueTitle || ''} onChange={(e) => patch({ issueTitle: e.target.value })} disabled={isSent} style={S.input} />
            </Field>
            <Field label="Issue month">
              <input data-testid="nl-issue-month" type="text" placeholder="May 2026" value={nl.issueMonth || ''} onChange={(e) => patch({ issueMonth: e.target.value })} disabled={isSent} style={S.input} />
            </Field>
            <Field label="Tagline (optional)" full>
              <input data-testid="nl-tagline" type="text" placeholder="Smarter benefits. Better outcomes." value={nl.tagline || ''} onChange={(e) => patch({ tagline: e.target.value })} disabled={isSent} style={S.input} />
            </Field>
            <Field label="Hero image URL (optional)" full>
              <input data-testid="nl-hero-image" type="text" placeholder="https://..." value={nl.heroImageUrl || ''} onChange={(e) => patch({ heroImageUrl: e.target.value })} disabled={isSent} style={S.input} />
            </Field>
          </div>
        </SectionCard>

        {/* Intro */}
        <SectionCard step="2" title="From the team" disabled={isSent}>
          <Field label="Heading">
            <input data-testid="nl-intro-heading" type="text" value={nl.intro?.heading || ''} onChange={(e) => patch({ intro: { ...nl.intro, heading: e.target.value } })} disabled={isSent} style={S.input} />
          </Field>
          <Field label="Body (Markdown supported)">
            <textarea
              data-testid="nl-intro-body"
              value={nl.intro?.body || ''}
              onChange={(e) => patch({ intro: { ...nl.intro, body: e.target.value } })}
              rows={5}
              disabled={isSent}
              placeholder={'Hi team,\n\nThis month we shipped **big news** about our jurisdiction engine and more.'}
              style={{ ...S.input, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, resize: 'vertical' }}
            />
          </Field>
        </SectionCard>

        {/* Stories */}
        <SectionCard
          step="3"
          title={`Stories (${(nl.stories || []).length})`}
          disabled={isSent}
          right={
            !isSent && (
              <button type="button" data-testid="nl-add-story" onClick={addStory} style={{ ...S.buttonPrimary, padding: '6px 10px', fontSize: 12 }}>
                + Add Story
              </button>
            )
          }
        >
          {(nl.stories || []).length === 0 && (
            <div style={{ color: C.textLight, fontStyle: 'italic', fontSize: 13, padding: 12 }}>
              No stories yet. Click <strong>+ Add Story</strong> to write the first one.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(nl.stories || []).map((s, idx, arr) => (
              <StoryBlock
                key={s.id}
                story={s}
                index={idx}
                total={arr.length}
                disabled={isSent}
                onPatch={(fields) => patchStory(s.id, fields)}
                onMove={(dir) => moveStory(s.id, dir)}
                onRemove={() => removeStory(s.id)}
              />
            ))}
          </div>
        </SectionCard>

        {/* What's Coming */}
        <SectionCard step="4" title="What's coming" disabled={isSent}>
          <Field label="Heading">
            <input data-testid="nl-wc-heading" type="text" value={nl.whatsComing?.heading || ''} onChange={(e) => patch({ whatsComing: { ...nl.whatsComing, heading: e.target.value } })} disabled={isSent} style={S.input} />
          </Field>
          <Field label="Items (bullet list)">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(nl.whatsComing?.items || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <input
                    data-testid={`nl-wc-item-${i}`}
                    type="text"
                    value={item}
                    onChange={(e) => patchWcItem(i, e.target.value)}
                    placeholder={`Upcoming item ${i + 1}`}
                    disabled={isSent}
                    style={{ ...S.input, flex: 1 }}
                  />
                  <button
                    type="button"
                    data-testid={`nl-wc-remove-${i}`}
                    onClick={() => removeWcItem(i)}
                    disabled={isSent || (nl.whatsComing?.items || []).length <= 1}
                    style={{ ...S.buttonSecondary, padding: '6px 10px', fontSize: 11, opacity: ((nl.whatsComing?.items || []).length <= 1) ? 0.3 : 1 }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid="nl-wc-add"
                onClick={addWcItem}
                disabled={isSent}
                style={{ ...S.buttonSecondary, padding: '6px 10px', fontSize: 12, alignSelf: 'flex-start' }}
              >
                + Add item
              </button>
            </div>
          </Field>
        </SectionCard>

        {/* Footer */}
        <SectionCard step="5" title="Footer" disabled={isSent}>
          <Field label="Tagline">
            <input data-testid="nl-footer-tagline" type="text" value={nl.footer?.tagline || ''} onChange={(e) => patch({ footer: { ...nl.footer, tagline: e.target.value } })} disabled={isSent} style={S.input} />
          </Field>
          <Field label="Contact (email or URL)">
            <input data-testid="nl-footer-contact" type="text" placeholder="mailto:hello@healthcues.com" value={nl.footer?.contactLink || ''} onChange={(e) => patch({ footer: { ...nl.footer, contactLink: e.target.value } })} disabled={isSent} style={S.input} />
          </Field>
        </SectionCard>
      </div>

      {/* RIGHT: Live preview */}
      <div style={{ position: 'sticky', top: 0 }}>
        <div style={{ ...S.card, padding: '12px 14px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Live preview
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                Email as it will arrive
              </div>
            </div>
            {previewLoading && <div style={{ fontSize: 11, color: C.textLight }}>Updating…</div>}
            {!serverId && <div style={{ fontSize: 11, color: C.textLight, fontStyle: 'italic' }}>Save the draft to refresh preview</div>}
          </div>
        </div>
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', background: '#f1f5f9', height: 'calc(100vh - 180px)', minHeight: 500 }}>
          {previewHtml ? (
            <iframe
              title="Newsletter preview"
              data-testid="nl-preview-iframe"
              srcDoc={previewHtml}
              style={{ width: '100%', height: '100%', border: 'none', background: '#f1f5f9' }}
            />
          ) : (
            <div style={{ padding: 28, color: C.textLight, fontStyle: 'italic', fontSize: 13 }}>
              Save the draft to generate the first preview. The preview updates automatically as you edit.
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <ConfirmSendModal
          recipientCount={audience?.total || 0}
          byAudience={audience?.byAudience || {}}
          subject={`${nl.issueTitle}${nl.issueMonth ? ' · ' + nl.issueMonth : ''}`}
          sending={sending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleSend}
        />
      )}
    </div>
  );
}

// ---- helpers + sub-components ----

function emptyStory() {
  return { id: tempStoryId(), heading: '', body: '', imageUrl: '', ctaLabel: '', ctaUrl: '' };
}

function SectionCard({ step, title, right, disabled, children }) {
  return (
    <div style={{ ...S.card, position: 'relative', opacity: disabled ? 0.85 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {step && (
            <span style={{ width: 24, height: 24, borderRadius: '50%', background: C.accent, color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace" }}>{step}</span>
          )}
          <div style={{ ...S.heading, margin: 0 }}>{title}</div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', marginBottom: 8 }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

function StoryBlock({ story, index, total, disabled, onPatch, onMove, onRemove }) {
  return (
    <div data-testid={`story-${story.id}`} style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', background: C.card }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: C.bg, borderBottom: `1px solid ${C.borderLight}` }}>
        <span style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Story #{index + 1}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" data-testid={`story-up-${story.id}`} disabled={disabled || index === 0} onClick={() => onMove('up')} style={{ ...iconBtn, opacity: (disabled || index === 0) ? 0.3 : 1 }}>▲</button>
        <button type="button" data-testid={`story-down-${story.id}`} disabled={disabled || index === total - 1} onClick={() => onMove('down')} style={{ ...iconBtn, opacity: (disabled || index === total - 1) ? 0.3 : 1 }}>▼</button>
        <button type="button" data-testid={`story-delete-${story.id}`} disabled={disabled} onClick={onRemove} style={{ ...S.buttonDanger, padding: '4px 10px', fontSize: 11 }}>Delete</button>
      </div>
      <div style={{ padding: 12, display: 'grid', gap: 8 }}>
        <Field label="Story heading">
          <input data-testid={`story-${story.id}-heading`} type="text" value={story.heading || ''} onChange={(e) => onPatch({ heading: e.target.value })} disabled={disabled} style={S.input} />
        </Field>
        <Field label="Image URL (optional)">
          <input data-testid={`story-${story.id}-image`} type="text" placeholder="https://..." value={story.imageUrl || ''} onChange={(e) => onPatch({ imageUrl: e.target.value })} disabled={disabled} style={S.input} />
        </Field>
        <Field label="Body (Markdown supported)">
          <textarea
            data-testid={`story-${story.id}-body`}
            rows={4}
            value={story.body || ''}
            onChange={(e) => onPatch({ body: e.target.value })}
            disabled={disabled}
            style={{ ...S.input, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, resize: 'vertical' }}
            placeholder="Tell the story. Use **bold**, *italics*, [links](https://...), or bullet lists."
          />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Field label="CTA label (optional)">
            <input data-testid={`story-${story.id}-cta-label`} type="text" placeholder="Learn more" value={story.ctaLabel || ''} onChange={(e) => onPatch({ ctaLabel: e.target.value })} disabled={disabled} style={S.input} />
          </Field>
          <Field label="CTA URL">
            <input data-testid={`story-${story.id}-cta-url`} type="text" placeholder="https://..." value={story.ctaUrl || ''} onChange={(e) => onPatch({ ctaUrl: e.target.value })} disabled={disabled} style={S.input} />
          </Field>
        </div>
      </div>
    </div>
  );
}

const iconBtn = {
  background: '#fff',
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  width: 22,
  height: 22,
  fontSize: 10,
  cursor: 'pointer',
  color: C.textMid,
};

function ConfirmSendModal({ recipientCount, byAudience, subject, sending, onCancel, onConfirm }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(2px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div data-testid="nl-confirm-send-modal" style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${C.border}`, boxShadow: '0 12px 40px rgba(0,0,0,0.16)', width: '100%', maxWidth: 480 }}>
        <div style={{ ...S.heading, marginBottom: 8 }}>Send to everyone?</div>
        <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>
          You're about to send <strong>{recipientCount} newsletter{recipientCount === 1 ? '' : 's'}</strong>.
        </div>
        <div style={{ fontSize: 12, color: C.textMid, marginBottom: 12 }}>
          Subject: <strong style={{ color: C.text }}>{subject}</strong>
        </div>
        <div style={{ background: C.bg, borderRadius: 8, padding: 10, fontSize: 12, color: C.textMid, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
            <span>Account Managers</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.text }}>{byAudience.account_manager || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
            <span>Employer Contacts</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.text }}>{byAudience.employer || 0}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
            <span>Employees</span><span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.text }}>{byAudience.employee || 0}</span>
          </div>
        </div>
        <div style={{ padding: 10, borderRadius: 8, background: C.orangeLight, color: C.orange, fontSize: 12, border: `1px solid ${C.orange}33` }}>
          <Icon name="alert-circle-outline" style={{ fontSize: 13, marginRight: 4, verticalAlign: '-2px' }} />
          Sent newsletters are <strong>locked</strong> and cannot be edited. A full copy is kept in History.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" data-testid="nl-confirm-cancel" onClick={onCancel} style={S.buttonSecondary}>Cancel</button>
          <button
            type="button"
            data-testid="nl-confirm-send"
            disabled={sending}
            onClick={onConfirm}
            style={{ ...S.buttonPrimary, opacity: sending ? 0.6 : 1 }}
          >
            {sending ? 'Sending…' : `Yes, send to ${recipientCount}`}
          </button>
        </div>
      </div>
    </div>
  );
}
